import { cn } from '@/lib/cn';
import { Divider, StarRating } from '@/components/ui';
import type { Building } from '@repo/api';

export interface BuildingInfoCardProps {
  building: Building;
  className?: string;
}

function formatBuildingDetails(building: Building) {
  const parts = [
    building.numUnits ? `${building.numUnits} units` : null,
    building.numFloors ? `${building.numFloors} floors` : null,
    building.builtYear ? `Built in ${building.builtYear}` : null,
  ].filter(Boolean);
  return parts as string[];
}

export function BuildingInfoCard({ building, className }: BuildingInfoCardProps) {
  const title = building.name || building.primaryAddress?.streetAddress || '';
  const subtitle =
    building.neighborhood?.name +
      ', ' +
      (building.primaryAddress?.fullAddress ?? '').split(',').slice(1).join(',').trim() || '';
  const details = formatBuildingDetails(building);
  const rating = building.averageRating || 0;
  const reviewCount = building.reviewCount || 0;

  return (
    <div
      className={cn(
        'bg-primary border border-tertiary rounded-3xl shadow',
        'pt-6 pb-3 px-0',
        className,
      )}
    >
      <div className={cn('flex flex-col gap-1 items-center w-full', 'px-4')}>
        <div className={cn('flex flex-col gap-2 items-center w-full', 'pb-3 pt-0 px-2')}>
          <div className={cn('flex flex-col gap-1 items-start w-full', 'text-primary')}>
            <h2 className="text-heading-6">{title}</h2>
            <p className="text-body-2-high">{subtitle}</p>
          </div>

          <div className={cn('flex gap-1 items-center w-full', 'text-body-2 text-secondary')}>
            {details.map((d, idx) => (
              <span key={`${d}-${idx}`} className={cn('flex items-center gap-1')}>
                {idx > 0 ? <span>·</span> : null}
                <span className={cn('overflow-hidden text-ellipsis')}>{d}</span>
              </span>
            ))}
          </div>
        </div>

        <div className={cn('w-full px-2')}>
          <Divider variant="tertiary" />
        </div>

        <div className={cn('flex items-center w-full', 'gap-4 px-2 py-3')}>
          <div className={cn('flex items-center justify-center', 'gap-3')}>
            {rating > 0 && <p className="text-heading-4 text-primary">{rating.toFixed(1)}</p>}
            <StarRating
              rating={rating}
              starSize={16}
              starSpace={4}
              fillClassName="icon-rating"
              emptyClassName="icon-tertiary"
            />
          </div>

          <Divider orientation="vertical" variant="tertiary" className="h-5" />

          <p className="text-body-2 text-secondary">
            {reviewCount > 0 ? `${reviewCount} reviews` : 'No reviews yet'}
          </p>
        </div>
      </div>
    </div>
  );
}
